import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import { Stack } from '@mui/material';
import {
    AddButtonWrapper,
    ContentDialogContentWrapper,
    ImageUploadValidationMessageWrapper
} from 'Components/Global.style';
import Api from 'Helpers/ApiHandler';
import { RESEND_OTP, VERIFY_OTP } from 'Helpers/ApiPaths';
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { SERVER_ERROR } from 'Helpers/Utils';

/**
 * @constant
 * @description API handler instance for making HTTP requests.
 *
 * @type {Api}
 */
const API = new Api({});

/**
 * @component
 * @description OTP verification modal component for password reset process.
 * Provides comprehensive OTP verification functionality including:
 * - 6-digit OTP input with individual character fields
 * - OTP validation and verification
 * - Resend OTP functionality
 * - Email masking for privacy
 * - Paste support for OTP codes
 * - Navigation between input fields
 * - Loading states for verification and resend
 * - Error handling and validation
 * - Professional UI with Material-UI components
 *
 * Key Features:
 * - Individual OTP digit input fields
 * - Automatic navigation between fields
 * - Paste support for OTP codes
 * - Email masking for security
 * - Resend OTP functionality
 * - Loading states during API calls
 * - Error handling with validation
 * - Professional authentication interface
 * - Keyboard navigation support
 * - Automatic focus management
 * - Real-time validation feedback
 *
 * @param {Object} props - Component props
 * @param {string} props.email - User email address for OTP verification
 * @param {Function} props.setOtpModalShow - Function to control modal visibility
 * @returns {JSX.Element} Complete OTP verification interface
 *
 * @example
 * // Used within ForgotPassword component
 * <OTPModal
 *   email="user@example.com"
 *   setOtpModalShow={setOtpModalShow}
 * />
 *
 * @example
 * // Component usage in password reset flow
 * function PasswordResetFlow() {
 *   const [showOTP, setShowOTP] = useState(false);
 *   const [email, setEmail] = useState('');
 *
 *   return (
 *     <div>
 *       {showOTP && (
 *         <OTPModal
 *           email={email}
 *           setOtpModalShow={setShowOTP}
 *         />
 *       )}
 *     </div>
 *   );
 * }
 *
 * @see RESEND_OTP - Resend OTP API endpoint
 * @see VERIFY_OTP - OTP verification API endpoint
 * @see SERVER_ERROR - Server error constant
 */
const OTPModal = ({ email, setOtpModalShow }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [reloading, setReLoading] = useState(false);

    const navigate = useNavigate();
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const inputRefs = useRef([]);

    /**
     * @function
     * @description Handles OTP input changes and automatic navigation.
     * Validates single digit input and moves focus to next field.
     *
     * @param {Event} e - Input change event
     * @param {number} index - Index of current input field
     * @returns {void}
     */
    const handleChange = (e, index) => {
        const value = e.target.value;
        if (value.length === 1 && !isNaN(value)) {
            // If input is a single digit, update the OTP state and move to next input
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);
            if (index < otp.length - 1) {
                inputRefs.current[index + 1].focus();
            }
        }
    };

    /**
     * @function
     * @description Handles OTP resend functionality.
     * Sends new OTP to user's email address.
     *
     * @param {string} values - User email address
     * @returns {void}
     *
     * @example
     * // Called when user clicks resend OTP
     * resendOtp('user@example.com');
     */
    const resendOtp = (values) => {
        setReLoading(true);
        API.post(`${RESEND_OTP}?email=${encodeURIComponent(values.replace(/\s/g, ''))}`)
            .then((response) => {
                setReLoading(false);
                toast.success(response.message);
            })
            .catch((error) => {
                setReLoading(false);
                toast.error(error?.response?.data?.message || SERVER_ERROR);
            });
    };

    /**
     * @function
     * @description Handles OTP verification process.
     * Validates OTP input and verifies with server.
     *
     * @param {string} email - User email address
     * @returns {void}
     *
     * @example
     * // Called when user clicks verify OTP
     * handleVerifyOtp('user@example.com');
     */
    const handleVerifyOtp = (email) => {
        if (otp.every((i) => i === '')) {
            setError(true);
        } else if (otp.some((i) => i === '')) {
            setError(true);
        } else {
            setError(false);
            const _otp = otp.splice(',').join('');
            setLoading(true);
            API.post(`${VERIFY_OTP}?otp=${_otp}`) // Join the OTP array into a string
                .then((response) => {
                    setLoading(false);
                    toast.success(response.message);
                    setOtpModalShow(false);
                    setOtp(['', '', '', '', '', '']);
                    navigate('/reset-password', { state: { email: email }, replace: true });
                })
                .catch((error) => {
                    setLoading(false);
                    setOtp(['', '', '', '', '', '']);
                    toast.error(error?.response?.data?.message || SERVER_ERROR);
                });
        }
    };

    /**
     * @function
     * @description Handles keyboard navigation and backspace functionality.
     * Manages focus movement and input clearing on backspace.
     *
     * @param {KeyboardEvent} e - Keyboard event
     * @param {number} index - Index of current input field
     * @returns {void}
     */
    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace') {
            const newOtp = [...otp];
            newOtp[index] = ''; // Clear the current input
            setOtp(newOtp);

            // If backspace is pressed and the current input is empty, focus on the previous input
            if (index > 0) {
                inputRefs.current[index - 1].focus();
            }
        }
    };

    /**
     * @function
     * @description Handles input focus and text selection.
     * Selects all text when input is focused and empty.
     *
     * @param {FocusEvent} e - Focus event
     * @param {number} index - Index of current input field
     * @returns {void}
     */
    const handleInputFocus = (e, index) => {
        if (otp[index] === '') {
            e.target.select(); // Select all text if the input is empty and focused
        }
    };

    /**
     * @function
     * @description Handles OTP paste functionality.
     * Processes pasted OTP codes and distributes digits to input fields.
     *
     * @param {ClipboardEvent} e - Paste event
     * @returns {void}
     */
    const handlePaste = (e) => {
        e.preventDefault();
        const clipboardData = e.clipboardData.getData('Text');
        // Extract only the first 6 digits if they exist in the clipboard
        const otpArray = clipboardData.split('').slice(0, otp.length);

        // Ensure that only numeric values are inserted into the OTP array
        const newOtp = otpArray.map((digit, index) => (/[0-9]/.test(digit) ? digit : otp[index]));

        // Set OTP state
        setOtp(newOtp);

        // Check if the clipboard contains valid OTP digits and focus on the last input field
        if (newOtp.length === otp.length && newOtp.every((digit) => digit !== '')) {
            inputRefs.current[otp.length - 1]?.focus();
        }
    };

    /**
     * @function
     * @description Masks email address for privacy display.
     * Shows first 3 characters and domain, masks the rest.
     *
     * @param {string} email - Email address to mask
     * @returns {string} Masked email address
     *
     * @example
     * // Returns: "use****@example.com"
     * maskEmail('user@example.com');
     */
    function maskEmail(email) {
        const emailParts = email.split('@');
        const username = emailParts[0];
        const domain = emailParts[1];

        // Masking the username part with stars
        const maskedUsername = username.slice(0, 3) + '****';

        return maskedUsername + '@' + domain;
    }

    return (
        <div>
            <Box style={{ marginTop: '20px', width: '500px' }}>
                <Box display="flex" justifyContent="space-between" flexDirection={'column'}>
                    <Typography variant="body1" style={{ fontWeight: 600 }}>
                        OTP Sent to {maskEmail(email)}
                    </Typography>
                    <Typography variant="body2" style={{ marginTop: 5 }}>
                        We&apos;ve sent a One-Time Password (OTP) to your email. Please enter the
                        code to verify your identity.
                    </Typography>
                    <Stack direction={'row'} justifyContent={'space-between'} gap={5} marginTop={4}>
                        {otp.map((digit, index) => (
                            <input
                                key={index}
                                ref={(el) => (inputRefs.current[index] = el)}
                                type="text"
                                maxLength="1"
                                value={digit}
                                onChange={(e) => handleChange(e, index)}
                                onKeyDown={(e) => handleKeyDown(e, index)}
                                onFocus={(e) => handleInputFocus(e, index)}
                                onPaste={handlePaste}
                                style={{
                                    width: '100%',
                                    height: '45px',
                                    textAlign: 'center',
                                    fontSize: '20px',
                                    border: error ? '1px solid red' : '1px solid #ccc',
                                    borderRadius: '5px'
                                }}
                            />
                        ))}
                    </Stack>
                    {error && (
                        <ImageUploadValidationMessageWrapper style={{ marginTop: 10 }}>
                            Please enter a valid OTP.
                        </ImageUploadValidationMessageWrapper>
                    )}
                    <AddButtonWrapper
                        fullWidth
                        sx={{ mt: 3, mb: 2 }}
                        onClick={() => handleVerifyOtp(email)}>
                        {loading ? (
                            <CircularProgress color="inherit" size={'30px'} />
                        ) : (
                            'Verify now'
                        )}
                    </AddButtonWrapper>
                </Box>
            </Box>
            <ContentDialogContentWrapper>
                <Stack alignItems={'flex-end'}>
                    <Box>
                        <Button variant="text" color="primary" onClick={() => resendOtp(email)}>
                            {reloading ? (
                                <CircularProgress color="inherit" size={'20px'} />
                            ) : (
                                'Resend Otp'
                            )}
                        </Button>
                    </Box>
                </Stack>
            </ContentDialogContentWrapper>
        </div>
    );
};

export default OTPModal;
